import axios from 'axios';
import { getAPIErrorReason, isDev, isEmail, isEmpty } from '../helpers/common';
import UserPreferenceSingleton from '../helpers/userPreferenceSingleton';
import {
  AppSettingDetailsInterface,
  BrandDomainDetailsInterface,
  EmailDetailInterface,
  InviteNewUserInterface,
  InvoicingDetailsInterface,
  NotificationSettingDetailsInterface,
  ProfileDataInterface,
  TaskTemplateDataInterface,
  SubscriptionUrlPayloadInterface,
  TeamcampAIUsageItemInterface,
  ApiResponseInterface
} from '../interfaces/SettingsInterface';
import { REACT_APP_TEAMCAMP_APIURL } from '../global/environment';
import { trackAnalyticActivity } from './analyticsService';
import { captureException } from './logService';
import { DEFAULT_DATE_FORMAT } from '../global/constants';
import { WORKSPACE_ANALYTICS } from '../global/analyticsConstants';
import { rootStore } from '../mobx/rootStore';
import { formatToProjectTemplateStore, formatToTaskTemplateStore } from '../mobx/common';
import dayjs from 'dayjs';
import { ProjectTemplateInput } from '../mobx/interfaces/project';

/**
 * Invites a new user to join the team/company by sending an email invitation
 *  @param {*}
 */
export const inviteNewUser = async (payloadData: InviteNewUserInterface) => {
  try {
    rootStore.settingStore.setLoading(true);
    // Check for email empty and valid email validation
    if (isEmpty(payloadData?.email)) {
      dispatchSettingError('Please enter email!');
      return;
    } else if (!isEmail(payloadData?.email)) {
      dispatchSettingError('Please enter valid email!');
      return;
    }
    // Set payload data
    const payload = {
      ...payloadData,
      isDev: isDev()
    };
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/company/InviteUsers`, payload);
    if (response?.data?.error) {
      dispatchSettingError(response.data.error);
      return false;
    } else if (response) {
      // Always return a truthy value on success, even when the api sends back no data
      return response?.data?.data ?? true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to invite user please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Removes a invite user from the team
 * @param {*}
 */
export const deleteInvitedUser = async (userId?: string) => {
  try {
    rootStore.settingStore.setLoading(true);
    if (!userId) {
      dispatchSettingError('User id is required');
      return false;
    }
    const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
    if (!userDetails?.id) {
      dispatchSettingError('Current user not found');
      return false;
    }
    const params = {
      userId: userDetails?.id,
      deleteUserId: userId
    };
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/company/removeCompanyUser`, {
      params
    });
    if (response) {
      trackAnalyticActivity(WORKSPACE_ANALYTICS.USER_REMOVED);
      dispatchSettingSuccess(response.data.message);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to delete invited user please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Fetches user profile settings data from the API
 * @param {*}
 */
export const getProfileSettingData = async () => {
  try {
    rootStore.settingStore.setLoading(true);
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/User/getUserDetails`);
    const { data } = response;
    if (data) {
      const updatedData = {
        ...data,
        dateFormat: !isEmpty(data?.dateFormat) ? data?.dateFormat : DEFAULT_DATE_FORMAT
      };
      rootStore.settingStore.setProfileData(updatedData);
      return updatedData;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get profile setting data please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc setting - Updates user profile information

 * @param {*}
 */
export const updateProfileSetting = async (payloadData: ProfileDataInterface) => {
  try {
    rootStore.settingStore.setLoading(true);
    const payload = {
      ...payloadData
    };
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/User/updateProfile`, payload);
    if (response) {
      if (response.data.message) {
        dispatchSettingSuccess(response.data.message);
        return true;
      } else if (response.data.error) {
        dispatchSettingError(response.data.error);
        return false;
      }
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update profile setting data please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc  Updates user's email address after validating current password
 * @param {*}
 */
export const updateEmailData = async (payloadData: EmailDetailInterface) => {
  try {
    rootStore.settingStore.setLoading(true);
    if (isEmpty(payloadData?.email)) {
      rootStore.alertNotificationStore.setErrorMessage('Please enter email.');
      return;
    } else if (isEmpty(payloadData?.password)) {
      rootStore.alertNotificationStore.setErrorMessage('Please enter password.');
      return;
    }
    const payload = {
      ...payloadData
    };
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/User/changeEmail`, payload);
    if (response) {
      dispatchSettingSuccess(response.data.message);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update email details please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting - Changes the role of a user to admin in the workspace
 * @param {*}
 */
export const changeUserAdmin = async (user_id: string) => {
  try {
    rootStore.settingStore.setLoading(true);
    if (isEmpty(user_id)) {
      dispatchSettingError('Invalid user selected');
      return false;
    }
    const payload = {
      target_user_id: user_id
    };
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/company/putUserAdmin`, payload);
    if (!isEmpty(response?.data?.error)) {
      dispatchSettingError(response?.data?.error);
    } else if (response?.data?.message) {
      dispatchSettingSuccess(response?.data?.message);
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to change user role please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting - Handles the Auth redirect flow for connecting a Stripe account to a workspace
 * @param {*}
 */
export const stripeRedirect = async (
  paramsObj: {
    user_id: string | null;
    account_id: string | null;
    install_signature: string | null;
    stripe_user_id: string | null;
    state?: string | null;
    livemode: boolean;
  },
  workspaceId?: string
) => {
  try {
    rootStore.settingStore.setLoading(true);
    // Prepare parameters for the API call
    const params: {
      user_id: string;
      account_id: string;
      state?: string;
      install_signature: string;
      workspace_id: string;
      stripe_user_id: string;
      livemode: boolean;
    } = {
      user_id: paramsObj?.user_id || '',
      account_id: paramsObj?.account_id || '',
      state: paramsObj?.state || '',
      install_signature: paramsObj?.install_signature || '',
      workspace_id: workspaceId || '',
      stripe_user_id: paramsObj?.stripe_user_id || '',
      livemode: paramsObj?.livemode
    };
    // Make API call to complete Stripe connection
    const response: ApiResponseInterface = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/stripe/stripeRedirect`, {
      params
    });
    // Handle error response from API
    if (response.data.error) {
      dispatchSettingError(response.data.error);
      return false;
    }
    return true;
  } catch (e) {
    // Handle any errors during the process
    const error = getAPIErrorReason(e) || 'Unable to set stripe data';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting - Disconnects Stripe payment integration from the specified workspace
 * @param {*}
 */
export const disconnectStripe = async () => {
  try {
    rootStore.settingStore.setLoading(true);
    // Make API call to disconnect Stripe account
    const response: ApiResponseInterface = await axios.get(
      `${REACT_APP_TEAMCAMP_APIURL}/stripe/disconnectStripeAccount`
    );
    // Check if there's an error in the response
    if (response.data.error) {
      dispatchSettingError(response?.data?.error);
      return false;
    }
    return true;
  } catch (e) {
    // Handle any errors that occur during the process
    const error = getAPIErrorReason(e) || 'Unable to disconnect stripe account';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting - get API Key List
 * @param {*}
 */
export const getApiKeyList = async () => {
  try {
    rootStore.settingStore.setLoading(true);
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/apiKey/getApiKeyList`);
    // Extract data from response
    const { data } = response;
    // If data exists, update Redux store with API key list
    if (data) {
      return data;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get API key list. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting -  Creates a new API key
 * @param {*}
 */
export const createApiKey = async (title: string) => {
  try {
    rootStore.settingStore.setLoading(true);

    // Validate if title is provided
    if (isEmpty(title)) {
      dispatchSettingError('Please enter api key name.');
      return;
    }
    // Prepare payload for API request
    const payload = {
      title
    };
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/apiKey/createApiKey`, payload);
    // Handle API response
    if (!isEmpty(response?.data?.error)) {
      dispatchSettingError(response?.data?.error);
    } else if (response) {
      dispatchSettingSuccess(response?.data?.message);
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to create API key please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting - Deletes an API key by making a DELETE request to the server
 * @param {*}
 */
export const deleteApiKey = async (apiKey_Id: string) => {
  try {
    rootStore.settingStore.setLoading(true);
    // Prepare parameters for the API request
    const params = {
      apyKeyId: apiKey_Id
    };
    const response = await axios.delete(`${REACT_APP_TEAMCAMP_APIURL}/apiKey/deleteApiKey`, { params });
    // Handle error response from the API
    if (!isEmpty(response?.data?.error)) {
      dispatchSettingError(response?.data?.error);
    } else if (response) {
      dispatchSettingSuccess(response?.data?.message);
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to Delete key please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting - Handles Figma Auth redirect callback
 * @param {*}
 */
export const figmaConnectRedirect = async (paramsObj: { code: string | null; state?: string | null }) => {
  try {
    rootStore.settingStore.setLoading(true);

    // Prepare parameters for the API call
    const params: {
      code: string;
    } = {
      code: paramsObj?.code || ''
    };
    const response: ApiResponseInterface = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/figma/figmaRedirect`, {
      params
    });

    // Handle error response from the API
    if (response.data.error) {
      dispatchSettingError(response.data.error);
      return false;
    }

    return true;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to set figma redirect details';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    // Always hide loading state when done
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Updates company app settings
 * @param {*}
 */
export const updateAppsSettings = async (payload: AppSettingDetailsInterface) => {
  try {
    // Make POST request to update app settings
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/company/putAppsSetting`, payload);
    if (response) {
      dispatchSettingSuccess('Updated successfully!');
      return true;
    }
  } catch (e) {
    // Handle error case
    const error = getAPIErrorReason(e) || 'Unable to update apps settings please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  }
};

/**
 * @desc Setting - Fetch Subscription List
 * @param {*}
 */
export const getSubscriptionList = async () => {
  try {
    rootStore.settingStore.setLoading(true);
    // Make API call to fetch active and past subscriptions
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/admin/subscription/getActivePastSubscriptions`);
    const { data } = response;
    // If data is received successfully, update the subscription list in store
    if (data) {
      rootStore.settingStore.setSubscriptionList(data);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get subscription list. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting - Opens the Stripe Customer Billing Portal for a live subscription
 * @param {string} customerId - Stripe customer id from the subscription row
 */
export const createBillingPortalSession = async (customerId: string): Promise<string | undefined> => {
  try {
    const response = await axios.post<{ url: string }>(
      `${REACT_APP_TEAMCAMP_APIURL}/stripe/createBillingPortalSession`,
      {
        customerId,
        returnUrl: window.location.href
      }
    );
    return response?.data?.url;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to open the billing portal. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  }
};

/**
 * @desc Setting - Fetches and updates invoice settings for a specific workspace
 * @param {*}
 */
export const getInvoicingSettingsData = async (workspace_id: string | undefined) => {
  try {
    rootStore.settingStore.setLoading(true);
    if (!workspace_id) return undefined;
    // Make API call to fetch invoice settings for the given workspace
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/company/getPutInvoiceSetting/${workspace_id}`);
    const { data } = response;
    if (data) {
      rootStore.settingStore.setInvoiceSetting(data);
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get Invoicing details please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting -  Updates invoice settings for a workspace with provided details
 * @param {*}
 */
export const updateInvoicingSettings = async (workspace_id: string | undefined, payload: InvoicingDetailsInterface) => {
  try {
    rootStore.settingStore.setLoading(true);
    if (!workspace_id) return false;
    // Get current user details and add userId to the payload
    const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
    payload.userId = userDetails?.id;
    const response = await axios.put(
      `${REACT_APP_TEAMCAMP_APIURL}/company/getPutInvoiceSetting/${workspace_id}`,
      payload
    );
    if (response) {
      // Show success message and return true on successful update
      dispatchSettingSuccess(response.data.message);
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update invoicing details please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting - Creates a new project template with the given workspace ID, name and description
 * @param {*}
 */
export const createProjectTemplate = async (
  workspace_id: string | undefined,
  name: string | undefined,
  description: string | undefined
) => {
  try {
    rootStore.settingStore.setLoading(true);
    // Prepare payload with required template details
    const payload = {
      name: name,
      companyId: workspace_id,
      description: description
    };
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/projectTemplate/postProjectTemplate`, payload);
    if (response) {
      // Check if there's any error in response
      if (!isEmpty(response?.data?.error)) {
        dispatchSettingError(response?.data?.error);
        return false;
      } else {
        dispatchSettingSuccess('Project template created succesfully!');
        return true;
      }
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to create Project Template please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting - Retrieves list of project templates for a workspace, optionally formatted for project creation
 * @param {*}
 */
export const getProjectTemplateList = async (workspace_id: string | undefined, isForceLoad = true) => {
  try {
    rootStore.settingStore.setLoading(true);

    if (!isForceLoad && !isEmpty(rootStore.projectTemplateStore.projectTemplates)) {
      return rootStore.projectTemplateStore.projectTemplates;
    }

    // Fetch project templates for the given workspace
    const response = await axios.get(
      `${REACT_APP_TEAMCAMP_APIURL}/projectTemplate/getProjectTemplateList/${workspace_id}`
    );
    const { data } = response;
    if (data) {
      const formattedData = formatToProjectTemplateStore(data);
      rootStore.projectTemplateStore.setProjectTemplates(formattedData);
    }
    return data;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get Project Template list';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting - Updates an existing project template with new details
 * @param {*}
 */
export const saveProjectTemplateDetails = async (payload: ProjectTemplateInput) => {
  try {
    rootStore.settingStore.setLoading(true);
    // Update project template with new details
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/projectTemplate/putProjectTemplate/${payload?.id}`, {
      ...(payload || {})
    });
    const { data } = response;
    if (data) {
      dispatchSettingSuccess('Template updated successfully');
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to save project template details';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting - Removes a project template from the system
 * @param {*}
 */
export const deleteProjectTemplate = async (templateId: string) => {
  try {
    rootStore.settingStore.setLoading(true);
    // Delete project template by ID
    const response = await axios.delete(
      `${REACT_APP_TEAMCAMP_APIURL}/projectTemplate/deleteProjectTemplate/${templateId}`
    );
    if (!isEmpty(response?.data?.error)) {
      dispatchSettingError(response?.data?.error);
    } else if (response) {
      dispatchSettingSuccess(response?.data);
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to Delete template. please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting -  Fetches the user's referral code
 * @param {*}
 */
export const getUserReferCode = async () => {
  try {
    rootStore.settingStore.setLoading(true);
    // Make API call to get user's referral code
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/User/getUserRefCode`);
    const { data } = response;
    // Return the referral code if data exists
    if (data) {
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get user refer code. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting ->  Fetches and returns the user's referral details
 * @param {*}
 */
export const getReferralDetails = async () => {
  try {
    rootStore.settingStore.setLoading(true);
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/User/getReferralDetails`);
    const { data } = response;
    // If data exists, update the store with referral details and return the data
    if (data) {
      return data;
    }
    return undefined;
  } catch (e) {
    // Handle any errors that occur during the API call
    const error = getAPIErrorReason(e) || 'Unable to get Referral Details. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting -  Fetches the current branding details from the API
 * @param {*}
 */
export const getBrandingDetails = async () => {
  try {
    rootStore.settingStore.setLoading(true);
    // Fetch branding details from the API
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/branding/getBrandingDetails`);
    const { data } = response;
    if (data) {
      return data;
    }
  } catch (e) {
    // Handle error and log it for monitoring
    const error = getAPIErrorReason(e) || 'Unable to get branding details. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting - Updates the branding details with new information
 * @param {*}
 */
export const updateBrandingDetails = async (payload: BrandDomainDetailsInterface) => {
  try {
    rootStore.settingStore.setLoading(true);
    // Update branding details through API
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/branding/updateBrandingDetails`, payload);
    const { data } = response;
    if (data) {
      // Show success message and return updated data
      dispatchSettingSuccess('Branding data updated successfully');
      return data;
    }
  } catch (e) {
    // Handle error and log it for monitoring
    const error = getAPIErrorReason(e) || 'Unable to update branding details. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting - Disconnects the Figma account integration from the current workspace
 * @param {*}
 */
export const disconnectFigmaAccount = async () => {
  try {
    rootStore.settingStore.setLoading(true);
    // Make API call to disconnect Figma account
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/figma/disconnectFigma`);
    // If successful, show success message and return true
    if (response) {
      dispatchSettingSuccess(response.data);
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to disconnect figma account please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting - Fetches user notification settings
 * @param {*}
 */
export const getNotificationSettingDetails = async () => {
  try {
    rootStore.settingStore.setLoading(true);
    // Make API call to fetch notification settings
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/User/userNotificationSettings`);
    const { data } = response;
    if (data) {
      // Update MobX store with notification settings and set updated flag to false
      rootStore.settingStore.setNotificationSettingDetails({ ...data, updated: false });
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get notification setting details. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting - Updates user notification settings
 * @param {*}
 */
export const updateNotificationSettingDetails = async (payload: NotificationSettingDetailsInterface) => {
  try {
    rootStore.settingStore.setLoading(true);
    // Make API call to update notification settings with new payload
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/User/userNotificationSettings`, payload);
    const { data } = response;
    if (data) {
      // Show success message if update was successful
      dispatchSettingSuccess('Notifications setting updated!');
      return true;
    }
    return false;
  } catch (e) {
    // Handle API errors and log them for monitoring
    const error = getAPIErrorReason(e) || 'Unable to update notification setting details. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting - template -  Creates a new task template with the provided payload data
 * @param {*}
 */
export const createNewTaskTemplate = async (payload: TaskTemplateDataInterface) => {
  try {
    rootStore.settingStore.setLoading(true);
    // Make API call to create task template
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/taskTemplate`, payload);
    const { data } = response;
    if (data) {
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to create task template. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting - template -  Retrieves the list of all task templates
 * @param {*}
 */
export const getTaskTemplateList = async () => {
  try {
    rootStore.settingStore.setLoading(true);
    // Make API call to get task templates
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/taskTemplate`);
    const { data } = response;
    if (data) {
      // Update Redux store with task template list
      const formatted = formatToTaskTemplateStore(data?.data);
      rootStore.projectTemplateStore.setTaskTemplateList(formatted);
      return formatted || [];
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get task template list. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting -template - Deletes a specific task template by its ID
 * @param {*}
 */
export const deleteTaskTemplate = async (templateId: string) => {
  try {
    rootStore.settingStore.setLoading(true);
    // Make API call to delete task template
    const response = await axios.delete(`${REACT_APP_TEAMCAMP_APIURL}/taskTemplate/${templateId}`);
    if (!isEmpty(response?.data?.error)) {
      // Handle API error response
      dispatchSettingError(response?.data?.error);
    } else if (response) {
      // Show success message
      dispatchSettingSuccess(response?.data?.message);
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to delete task template. please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting -template - Updates the details of a task template
 * @param {*}
 */
export const updateTaskTemplateDetails = async (taskId: string, payload: TaskTemplateDataInterface) => {
  try {
    rootStore.settingStore.setLoading(true);
    // Make API call to update task template
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/taskTemplate/${taskId}`, payload);
    const { data } = response;
    if (data) {
      // Show success message and return updated data
      dispatchSettingSuccess(' Task Template updated successfully');
      return data;
    }
    return undefined;
  } catch (e) {
    // Handle error and show error message
    const error = getAPIErrorReason(e) || 'Unable to update task template details';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc  Gets the Stripe subscription URL for the current workspace
 * @param {*}
 */
export const getSubscriptionUrl = async (payload: SubscriptionUrlPayloadInterface) => {
  try {
    rootStore.settingStore.setLoading(true);
    // Make API call to get Stripe subscription URL
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/stripe/getSubscriptionUrl`, payload);
    // Return subscription URL data if available
    if (response?.data) {
      return response?.data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get subscription url';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Invoice - Fetches and updates the company's Stripe integration status for the given workspace
 * @param {*}
 */
export const getCompanySettingIntegration = async (workSpace_Id: string | undefined) => {
  try {
    rootStore.settingStore.setLoading(true);
    if (!workSpace_Id) return false;
    // Make API call to get company's Stripe integration status
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/company/integrationStatus`, {
      headers: { workspace_id: workSpace_Id }
    });
    const { data } = response;
    if (data) {
      rootStore.settingStore.setSettingIntegration(data);
      return data;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || `Unable to get Company Stripe Account Status please try again`;
    captureException(error);
    dispatchSettingError(error);
    return undefined;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc sentry connect
 * @param {*}
 */
export const sentryAccountConnect = async (payload: { installationId: string; code: string }) => {
  try {
    rootStore.settingStore.setLoading(true);
    // Make API call to bind Sentry account
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/sentry/bind`, payload);
    // Return true if connection was successful
    if (response) return true;
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to add sentry account! please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    await rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting -  Fetches and returns a list of all active user sessions
 * @param {*}
 */
export const getSessionList = async () => {
  try {
    rootStore.settingStore.setLoading(true);

    // Make API call to fetch all active sessions
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/auth/getSessions`);
    const { data } = response;
    if (data) {
      // Update Redux store with the fetched session list
      return data;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get session list. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Setting - Revokes all active sessions for the current user
 * @param {*}
 */
export const removeAllSession = async () => {
  try {
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/auth/revokeAllSessions`);
    if (response) {
      // Show success message when all sessions are revoked
      dispatchSettingSuccess('remove all session successfully!');
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to remove all session list please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  }
};

/**
 * @desc Setting - Revokes a specific session using the provided session ID
 * @param {*}
 */
export const removeSession = async (payload: { sessionId: string }) => {
  try {
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/auth/revokeSession`, payload);
    if (response) {
      // Show success message when the specific session is revoked
      dispatchSettingSuccess('Session revoked successfully!');
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to remove session list please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  }
};

/**
 * @desc Transforms raw AI usage data into a report-friendly format.
 *       Maps user ID to workspace user name, formats createdAt into readable time,
 *       and formats usage cost into currency with 2 decimal points.
 */
export const updateAIUsageReportData = (data: TeamcampAIUsageItemInterface[]) => {
  return data.map((item: TeamcampAIUsageItemInterface) => {
    const userData = rootStore.workspaceStore.currentWorkspace?.users?.find((user) => user?.id === item?.userId);
    return {
      name: userData?.name || 'Unknown User',
      time: dayjs(item?.createdAt).format('MMMM D, HH:mm:ss'),
      cost: `$${Number(item?.usageUSD || 0).toFixed(2)}`
    };
  });
};

/**
 * @desc Fetches AI usage list data from the Teamcamp API with pagination.
 *       Handles loading state, errors, and returns the API response data.
 * @param {number} page - The page number for pagination (default = 1).
 * @param {number} limit - The number of items per page (default = 25).
 */
export const getTeamcampAIData = async (page = 1, limit = 50, append = false) => {
  try {
    rootStore.settingStore.setLoading(true);
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/openai/getUsageList`, {
      params: { page, limit }
    });
    const teacampAIDatalist = response.data?.data || [];
    const formattedData = updateAIUsageReportData(teacampAIDatalist) || [];

    if (append) {
      // Append new data to existing MobX store data
      rootStore.settingStore.setTeamcampAIData([...(rootStore.settingStore.teamcampAIData || []), ...formattedData]);
    } else {
      // Replace MobX store data for the first page load
      rootStore.settingStore.setTeamcampAIData(formattedData);
    }

    return formattedData;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get usage list. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

export function dispatchSettingError(msg: string) {
  rootStore.alertNotificationStore.setErrorMessage(msg);
}

export function dispatchSettingSuccess(msg: string) {
  rootStore.alertNotificationStore.setSuccessMessage(msg);
}
